import * as React from 'react';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { Kunde, useKundeList } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { cloneDeep, isEqual } from 'lodash';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import {
    Maschinenkunde,
    addMaschinenkunde,
    defaultMaschinenkunde,
    updateMaschinenkunde,
    useMaschinenkundeList,
} from 'api/maschinenkunde';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { logDifference } from 'utils/helper';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Maschinentype } from 'formComponents/lookups/Maschinentype';
import { Label } from '@fluentui/react/lib/Label';
import classnames from 'classnames';

import styles from './MaschinenkundeEdit.module.scss';

interface MaschinenkundeEditProps {
    kNr: number;
    lNr: number; // -1 = new
    onClose: (refetch: boolean) => void;
}

export const MaschinenkundeEdit: React.FC<MaschinenkundeEditProps> = ({ kNr, lNr, onClose }) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const isMounted = useIsMounted();

    const maschinenkundeList = useMaschinenkundeList({
        kNr,
        lNr,
        includeArchiv: true,
    });

    const [maschinenkunde, setMaschinenkunde] = React.useState<Maschinenkunde | null>(null);

    const kundeList = useKundeList({
        nr: kNr,
        includeArchiv: true,
    });

    const [kunde, setKunde] = React.useState<Kunde | null>(null);

    React.useEffect(() => {
        if (!kundeList.isLoading && !kundeList.isFetching && kundeList.data?.length === 1 && kunde === null) {
            setKunde(kundeList.data[0]);
        }
    }, [kunde, kundeList]);

    React.useEffect(() => {
        if (!maschinenkundeList.isLoading && !maschinenkundeList.isFetching && maschinenkunde === null) {
            if (maschinenkundeList.data?.length === 1) {
                setMaschinenkunde(cloneDeep(maschinenkundeList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setMaschinenkunde({ ...defaultMaschinenkunde, kNr });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [maschinenkunde, maschinenkundeList, kNr, auth]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !maschinenkundeList.isLoading &&
            !maschinenkundeList.isFetching &&
            maschinenkundeList.data?.length === 1 &&
            maschinenkunde !== null &&
            !isEqual(maschinenkundeList.data[0], maschinenkunde)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('maschinenkunde', maschinenkunde, maschinenkundeList.data[0]);
        }
    }, [savingState, maschinenkundeList, maschinenkunde]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const save = () => {
        if (maschinenkunde && savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            if (lNr === -1) {
                addMaschinenkunde(maschinenkunde)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            } else {
                updateMaschinenkunde(maschinenkunde)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    };

    const renderCheckbox = (column: keyof Maschinenkunde, label: string) => (
        <Checkbox
            className={formStyles.checkbox}
            label={label}
            disabled={savingState === SavingState.saving}
            checked={maschinenkunde?.[column] as boolean}
            onChange={(event, checked) => {
                if (maschinenkunde) {
                    setMaschinenkunde({
                        ...maschinenkunde,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderTextField = (column: keyof Maschinenkunde, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(maschinenkunde?.[column] || '') as string}
            savingState={savingState}
            options={options}
            onChange={(newValue): void => {
                if (maschinenkunde) {
                    setMaschinenkunde({
                        ...maschinenkunde,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof Maschinenkunde, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={maschinenkunde?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (maschinenkunde) {
                    setMaschinenkunde({
                        ...maschinenkunde,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderLDat = () => (
        <FormDatePicker
            label="Lieferdatum"
            savingState={savingState}
            value={maschinenkunde?.lDat}
            onChange={(newValue) => {
                if (maschinenkunde) {
                    setMaschinenkunde({
                        ...maschinenkunde,
                        lDat: newValue,
                    });
                }
            }}
        />
    );

    const renderDatAusgesch = () => (
        <FormDatePicker
            label="Ausgeschieden seit"
            savingState={savingState}
            value={maschinenkunde?.datAusgesch}
            onChange={(newValue) => {
                if (maschinenkunde) {
                    setMaschinenkunde({
                        ...maschinenkunde,
                        datAusgesch: newValue,
                    });
                }
            }}
        />
    );

    const renderMaschinentype = () => (
        <div>
            <Label>Maschinentype</Label>
            <Maschinentype
                value={maschinenkunde?.mtNr || 0}
                disabled={false}
                menuPlacement="bottom"
                allowEmpty
                onChange={(newMtNr) => {
                    if (maschinenkunde) {
                        setMaschinenkunde({
                            ...maschinenkunde,
                            mtNr: newMtNr,
                        });
                    }
                }}
            />
        </div>
    );

    return (
        <Dialog
            hidden={false}
            title={`Maschine bei ${kunde?.name1 || '...'} ${kunde?.name2 || ''}`}
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={800}
            onDismiss={() => {
                onClose(false);
            }}
        >
            {routerConfirmMessage.render()}
            {maschinenkundeList.isLoading ||
            maschinenkundeList.isFetching ||
            kundeList.isLoading ||
            kundeList.isFetching ||
            !maschinenkunde ||
            !kunde ? (
                <LoadingIndicator />
            ) : (
                <form className={formStyles.form}>
                    <div className={formStyles.topBlock}>
                        <div />
                        {renderCheckbox('archiv', 'Archiv')}
                    </div>

                    <div className={formStyles.grid3}>
                        <div className={classnames(formStyles.gridCell, styles.type)}>
                            {renderTextField('type', 'Type', { maxLength: 250 })}
                        </div>
                        {renderMaschinentype()}
                    </div>

                    <div className={formStyles.grid3}>
                        <div className={formStyles.gridCell}>
                            {renderTextField('hersteller', 'Hersteller', { maxLength: 30 })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('geraeteNr', 'Geräte-Nr.', { maxLength: 50 })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('artikelNr', 'Artikel-Nr.', { maxLength: 50 })}
                        </div>
                        <div />
                    </div>

                    <div className={formStyles.grid3}>
                        <div className={formStyles.gridCell}>
                            {renderTextField('baujahr', 'Baujahr', { numeric: true })}
                        </div>
                        <div className={formStyles.gridCell}>{renderLDat()}</div>
                        <div className={formStyles.gridCell}>{renderDatAusgesch()}</div>
                    </div>

                    <div className={formStyles.line}>{renderTextarea('bem', 'Bemerkungen')}</div>

                    <Timestamps
                        modifiedDate={maschinenkunde.updDat}
                        modifiedDecimalTime={maschinenkunde.updUhr}
                        modifiedUser={maschinenkunde.updUser}
                        showCreated={false}
                    />

                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                    <DialogFooter>
                        {savingState === SavingState.saving ? (
                            <PrimaryButton onClick={save} text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={save}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || maschinenkundeList.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
            )}
        </Dialog>
    );
};
